import React, { useState, useEffect } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ButtonGroup,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import { MdArrowDropDown, MdDelete, MdEdit } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import { FiPrinter } from "react-icons/fi";
import { TbFileDownload } from "react-icons/tb";
import CloseIcon from '@mui/icons-material/Close';
import { IoWarningOutline } from "react-icons/io5";

const Account = () => {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { accountId } = useParams();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');
  const [ready, setReady] = useState(false);
  const [noLabSelected, setNoLabSelected] = useState(false);

  // New state for confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');

  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(true);

  // Get LabId and User Type from localStorage on component mount
  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    const storedUserType = localStorage.getItem('userType');
    
    console.log('Loaded from localStorage - LabId:', storedLabId, 'UserType:', storedUserType);
    
    if (storedLabId) {
      setLabId(storedLabId);
      setNoLabSelected(false);
    } else {
      console.warn('No LabId found in localStorage');
      setNoLabSelected(true);
      setReady(true);
      setLoading(false);
      return;
    }
    
    if (storedUserType) {
      setUserType(storedUserType);
    } else {
      if (storedLabId) {
        setUserType('lab');
        console.log('Auto-setting userType to "lab" because LabId exists');
      } else {
        setUserType('admin');
        console.log('Auto-setting userType to "admin" because no LabId found');
      }
    }

    setReady(true);
  }, []);

  // CSV Export Utility Functions
  const convertToCSV = (data, columns) => {
    if (!data || data.length === 0) return "";
    const headers = columns.map((col) => `"${col.headerName}"`).join(",");
    const rows = data.map((row) => {
      return columns
        .map((col) => {
          const value = row[col.field] || "";
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",");
    });
    return [headers, ...rows].join("\n");
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Handlers
  const handleExportToExcel = () => {
    if (noLabSelected) {
      alert("Please select a lab first before exporting.");
      return;
    }
    const columnsForExport = [
      { field: "accountId", headerName: "Account ID" },
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "telephone", headerName: "Telephone" },
      { field: "email", headerName: "Email" },
      { field: "website", headerName: "Website" },
      { field: "time_zone", headerName: "Timezone" },
      { field: "status", headerName: "Status" },
      { field: "account_type", headerName: "Account Type" },
      { field: "addedBy", headerName: "Created By Lab" },
    ];
    const csvData = convertToCSV(filteredRows, columnsForExport);
    downloadCSV(
      csvData,
      `my_accounts_export_${new Date().toISOString().split("T")[0]}.csv`
    );
    handleClose();
  };

  const handleExportBillingAddresses = () => {
    if (noLabSelected) {
      alert("Please select a lab first before exporting.");
      return;
    }
    const billingColumns = [
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "status", headerName: "Status" },
    ];
    const csvData = convertToCSV(filteredRows, billingColumns);
    downloadCSV(
      csvData,
      `billing_addresses_${new Date().toISOString().split("T")[0]}.csv`
    );
    handleClose();
  };

  const handleExportShippingAddresses = () => {
    if (noLabSelected) {
      alert("Please select a lab first before exporting.");
      return;
    }
    const shippingColumns = [
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "status", headerName: "Status" },
    ];
    const csvData = convertToCSV(filteredRows, shippingColumns);
    downloadCSV(
      csvData,
      `shipping_addresses_${new Date().toISOString().split("T")[0]}.csv`
    );
    handleClose();
  };

  // Fetch accounts based on user type (Lab or Admin)
  const fetchAccounts = async () => {
    // Don't fetch if no lab is selected
    if (noLabSelected) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      console.log('Fetching accounts for:', { userType, labId });
      
      if (userType === 'lab' && labId) {
        // For Lab users: Fetch only their own accounts
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
        );
        
        console.log('Lab accounts response:', response.data);
        
        if (response.data.success) {
          const normalized = (response.data.data || [])
            .filter((account) => account && account.account_name)
            .map((account) => ({
              ...account,
              accountId: account.accountId || "N/A",
              account_name: account.account_name || "Unnamed",
              address: account.address || "N/A",
              address2: account.address2 || "N/A",
              city: account.city || "N/A",
              state: account.state || "N/A",
              zip: account.zip || "N/A",
              country: account.country || "N/A",
              telephone: account.telephone || "N/A",
              email: account.email || "N/A",
              website: account.website || "N/A",
              time_zone: account.time_zone || "N/A",
              account_type: account.account_type || "N/A",
              status: account.status || "Active",
              addedBy: account.addedBy || "Unknown",
              source: "HMS",
            }));
          console.log('Normalized lab accounts:', normalized.length);
          setRows(normalized);
        } else {
          setError(response.data.message || "Failed to fetch accounts.");
        }
      } else {
        // Only fetch all accounts if user is admin AND lab is selected
        // But based on your requirement, we should NOT show all labs data
        // So we'll just show empty array or error message
        setError("You must select a lab first to view accounts.");
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL HMS accounts (for admin users) - COMMENTED OUT as per requirement
  // We don't want to show all labs data when no lab is selected
  const fetchAllHMSAccounts = async () => {
    // This function is now disabled as per requirement
    return [];
  };

  // Fetch DDX accounts (for admin users) - COMMENTED OUT as per requirement
  const fetchDDXAccounts = async () => {
    // This function is now disabled as per requirement
    return [];
  };

  // Fetch data when ready and dependencies change
  useEffect(() => {
    if (ready && !noLabSelected && labId) {
      fetchAccounts();
    }
  }, [ready, labId, userType, noLabSelected]);

  // Handle delete click - Open confirmation dialog
  const handleDeleteClick = (id, source, accountName) => {
    if (noLabSelected) {
      alert("Please select a lab first before deleting accounts.");
      return;
    }
    
    // First check permissions
    if (userType !== 'admin') {
      const accountToDelete = rows.find(row => row._id === id);
      
      // Check if the logged-in lab owns this account
      if (accountToDelete && accountToDelete.addedBy !== labId) {
        alert("You can only delete accounts created by your lab.");
        return;
      }
    }
    
    setAccountToDelete({ id, source, accountName });
    setDeleteMessage(`Are you sure you want to delete account "${accountName}"?`);
    setOpenDeleteDialog(true);
  };

  // Confirm delete after dialog
  const confirmDelete = async () => {
    if (accountToDelete) {
      try {
        const { id, source } = accountToDelete;
        let response;
        
        if (source === "HMS") {
          response = await axios.delete(
            `https://hms.backend.hiragroupcloud.com/api/account/deleteAccount/${id}`
          );
        } else if (source === "DDX") {
          response = await axios.delete(`https://hms.backend.hiragroupcloud.com/api/users/${id}`);
        }
        
        if (response.data.success || response.status === 200) {
          setRows(rows.filter((row) => row._id !== id));
          // Show success message
          setError(null);
          const successAlert = document.createElement('div');
          successAlert.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: #d4edda;
            color: #155724;
            padding: 15px 20px;
            border-radius: 4px;
            border: 1px solid #c3e6cb;
            z-index: 9999;
            font-family: Arial, sans-serif;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          `;
          successAlert.textContent = `Account deleted successfully!`;
          document.body.appendChild(successAlert);
          
          setTimeout(() => {
            document.body.removeChild(successAlert);
          }, 3000);
        }
      } catch (error) {
        console.error("There was an error deleting the account:", error);
        setError("Error deleting account. Please try again.");
      }
    }
    setOpenDeleteDialog(false);
    setAccountToDelete(null);
  };

  // Cancel delete
  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setAccountToDelete(null);
  };

  const handleEdit = (account) => {
    if (noLabSelected) {
      alert("Please select a lab first before editing accounts.");
      return;
    }
    
    // Check if user has permission to edit
    if (userType === 'lab' && account.addedBy !== labId) {
      alert("You can only edit accounts created by your lab.");
      return;
    }
    
    navigate(`/TabsWorking/${account.accountId}`, {
      state: {
        accountName: account.account_name,
        accountData: account,
      },
    });
  };

  const filteredRows = !noLabSelected ? rows.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  ) : [];

  const columns = [
    { field: "account_name", headerName: "Account Name", width: 200 },
    { field: "city", headerName: "City", width: 150 },
    { field: "state", headerName: "State", width: 150 },
    { field: "country", headerName: "Country", width: 150 },
    { field: "account_type", headerName: "Type", width: 150 },
    { field: "status", headerName: "Status", width: 150 },
    ...(userType === 'admin' && !noLabSelected ? [
      { field: "addedBy", headerName: "Created By", width: 150 }
    ] : []),
    {
      field: "actions",
      headerName: "Actions",
      width: 500,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit(params.row)}
            style={{ backgroundColor: "#004aad", fontSize: "14px" }}
            disabled={noLabSelected || (userType === 'lab' && params.row.addedBy !== labId)}
            startIcon={<MdEdit />}
          >
            {userType === 'lab' && params.row.addedBy !== labId ? "View" : "Edit"}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#dc3545",
              "&:hover": { backgroundColor: "#c82333" },
              "&:disabled": { backgroundColor: "#cccccc" }
            }}
            onClick={() => handleDeleteClick(params.row._id, params.row.source, params.row.account_name)}
            disabled={noLabSelected || (userType === 'lab' && params.row.addedBy !== labId)}
            startIcon={<MdDelete />}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  // Handle dropdown menu
  const handleClick = (event) => {
    if (noLabSelected) {
      alert("Please select a lab first before accessing these options.");
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleMenuItemClick = (path) => {
    if (noLabSelected) {
      alert("Please select a lab first before navigating.");
      return;
    }
    navigate(path);
    handleClose();
  };

  // If no lab is selected, show message
  if (noLabSelected) {
  return (
      <div style={{textAlign: 'center', padding: '50px'}}>
          <h4>Please Select the lab to view accounts entries for the specific lab </h4>
        </div>
            );
  }

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '5px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" style={{ 
          backgroundColor: '#004aad', 
          color: 'white',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Confirm Delete</span>
          <IconButton
            aria-label="close"
            onClick={cancelDelete}
            style={{ color: 'white', padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          <DialogContentText 
            id="alert-dialog-description" 
            style={{ 
              fontSize: '16px', 
              color: '#333',
              textAlign: 'center',
              lineHeight: '1.5'
            }}
          >
            {deleteMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ 
          padding: '16px 24px', 
          justifyContent: 'flex-end',
          gap: '12px',
          borderTop: '1px solid #eee'
        }}>
          <Button
            onClick={cancelDelete}
            variant="outlined"
            style={{
              color: '#6c757d',
              borderColor: '#6c757d',
              padding: '8px 24px',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: '500'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 24px',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: '500'
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      <br />
      <div style={{ margin: "0 auto", padding: "0px" }}>
        <h2 className="title">Accounts</h2>
       
        <div className="search" style={{ padding: "10px" }}>
          <div className="row align-items-center">
            <div className="col-md-9 d-flex justify-content-between">
              <div className="col-md-3 d-flex justify-content-start">
                <TextField
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </div>
            </div>
            <div className="col-md-3 d-flex justify-content-end">
              <ButtonGroup variant="contained" aria-label="split button">
                <Button
                  onClick={() => navigate("/SearchAddPage")}
                  style={{
                    backgroundColor: "#004aad",
                    borderColor: "#004aad",
                  }}
                >
                  <FaPlus /> Add
                </Button>
                <Button
                  size="small"
                  aria-controls={
                    Boolean(anchorEl) ? "split-button-menu" : undefined
                  }
                  aria-expanded={Boolean(anchorEl) ? "true" : undefined}
                  aria-haspopup="menu"
                  onClick={handleClick}
                  style={{
                    backgroundColor: "#004aad",
                    borderColor: "#004aad",
                    padding: "0 8px",
                  }}
                >
                  <MdArrowDropDown style={{ fontSize: "24px" }} />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => handleMenuItemClick("/assign")}
                  style={{
                    color: "#004aad",
                    fontWeight: "bold",
                  }}
                >
                  <FaPlus /> Assign Price List Groups
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/assign-procedure")}
                  style={{
                    color: isDisabled ? "grey" : "initial",
                    pointerEvents: isDisabled ? "none" : "auto",
                  }}
                >
                  <FaPlus /> Assign Procedure Groups
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/assign-shipping")}
                  style={{
                    color: "#004aad",
                    fontWeight: "bold",
                  }}
                >
                  <FaPlus /> Assign Shipping Services
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/assign-tags")}
                  style={{
                    color: isDisabled ? "grey" : "initial",
                    pointerEvents: isDisabled ? "none" : "auto",
                  }}
                >
                  <FaPlus /> Assign Account Tags
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/account-default")}
                  style={{
                    color: isDisabled ? "grey" : "initial",
                    pointerEvents: isDisabled ? "none" : "auto",
                  }}
                >
                  <FaPlus /> New Account Defaults
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("/account-quick")}
                  style={{
                    color: isDisabled ? "grey" : "initial",
                    pointerEvents: isDisabled ? "none" : "auto",
                  }}
                >
                  <FiPrinter /> Account Quick Codes
                </MenuItem>
                <MenuItem
                  onClick={handleExportToExcel}
                  style={{
                    color: "#004aad",
                    fontWeight: "bold",
                  }}
                >
                  <TbFileDownload /> Export to Excel
                </MenuItem>
                <MenuItem
                  onClick={handleExportBillingAddresses}
                  style={{
                    color: "#004aad",
                    fontWeight: "bold",
                  }}
                >
                  <TbFileDownload /> Export Billing Addresses
                </MenuItem>
                <MenuItem
                  onClick={handleExportShippingAddresses}
                  style={{
                    color: "#004aad",
                    fontWeight: "bold",
                  }}
                >
                  <TbFileDownload /> Export Shipping Addresses
                </MenuItem>
              </Menu>
            </div>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            backgroundColor: "#f5f5f5",
            padding: "15px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          {loading ? (
            <CircularProgress
              sx={{ display: "block", margin: "20px auto" }}
            />
          ) : error ? (
            <Box sx={{ textAlign: 'center', padding: '20px', color: 'red' }}>
              <Typography>{error}</Typography>
            </Box>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row._id}
              autoHeight
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 7,
                  },
                },
              }}
              pageSizeOptions={[7, 15, 25, 50]}
              disableRowSelectionOnClick
              sx={{
                backgroundColor: "#fff",
                borderRadius: "5px",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f0f0f0",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ddd",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "visible !important",
                },
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Account;